// HeroSection.tsx
import * as React from "react";
import { Box, Typography, Button, Skeleton } from "@mui/material";
import { useQuery } from "@apollo/client";
import { HERO_SECTION_QUERY } from "../graphql/heroSection";
import { buildHeroImageUrl } from "../api/heroSection";
import { Helmet } from "react-helmet-async";

import ShopByCategory from "./shopByCategory";

export default function HeroSection() {
  const { data, loading } = useQuery(HERO_SECTION_QUERY);

  const hero = data?.heroSection;

  const leftImg = hero?.leftImage ? buildHeroImageUrl(hero.leftImage) : null;
  const rightImg = hero?.rightImage ? buildHeroImageUrl(hero.rightImage) : null;

  const heading = hero?.heading || "Crafted to Shine";
  const subheading = hero?.subheading || "Designed for Forever";
  const description =
    hero?.description ||
    "Discover timeless beauty and exceptional craftsmanship at Haroon Sharif Jewellers.";

  return (
    <>
      {/* Preload hero images so browser starts fetching before React renders */}
      {leftImg && (
        <Helmet>
          <link rel="preload" as="image" href={leftImg} />
        </Helmet>
      )}
      {rightImg && (
        <Helmet>
          <link rel="preload" as="image" href={rightImg} />
        </Helmet>
      )}
      <Box
        component="section"
        aria-label="Hero"
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
          alignItems: "stretch",
          minHeight: { xs: "auto", md: "65vh" },
          backgroundColor: "#fff",
        }}
      >
        {/* MOBILE IMAGE (xs only) */}
        <Box sx={{ display: { xs: "block", md: "none" }, width: "100%" }}>
          {leftImg ? (
            <Box
              component="img"
              src={leftImg}
              loading="eager"
              fetchPriority="high"
              alt="Haroon Sharif jewellery model"
              sx={{
                width: "100%",
                height: "auto",
                aspectRatio: "4 / 5",
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <Skeleton
              variant="rectangular"
              sx={{ width: "100%", aspectRatio: "4 / 5" }}
            />
          )}
        </Box>

        {/* DESKTOP LEFT IMAGE */}
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            width: "100%",
            height: "100%",
          }}
        >
          {leftImg ? (
            <Box
              component="img"
              src={leftImg}
              loading="eager"
              fetchPriority="high"
              alt="Haroon Sharif jewellery model"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <Skeleton
              variant="rectangular"
              sx={{ width: "100%", height: "100%" }}
            />
          )}
        </Box>

        {/* CENTER TEXT */}
        <Box
          sx={{
            order: { xs: 2, md: "initial" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            px: { xs: 3, md: 6 },
            py: { xs: 4, md: 0 },
            gap: 1.5,
          }}
        >
          {loading ? (
            <Skeleton width={280} height={54} />
          ) : (
            <Typography
              component="h1"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 500,
                lineHeight: 1.1,
                fontSize: { xs: 32, md: 46 },
                letterSpacing: 0.2,
                mb: 0.5,
              }}
            >
              {hero?.heading}
            </Typography>
          )}

          {loading ? (
            <Skeleton width={280} height={54} />
          ) : (
            <Typography
              component="h2"
              sx={{
                fontFamily: '"Playfair Display", serif',
                fontWeight: 400,
                lineHeight: 1.15,
                fontSize: { xs: 22, md: 28 },
                color: "rgba(0,0,0,0.8)",
                mb: 1,
              }}
            >
              {hero?.subheading}
            </Typography>
          )}

          {loading ? (
            <Skeleton width={280} height={54} />
          ) : (
            <Typography
              sx={{
                fontSize: { xs: 14.5, md: 16 },
                letterSpacing: 0.3,
                color: "#333",
                maxWidth: 520,
                mb: 2,
              }}
            >
              {hero?.description}
            </Typography>
          )}

          <Button
            variant="contained"
            onClick={() => {
              const section = document.getElementById("collection");
              if (section) section.scrollIntoView({ behavior: "smooth" });
            }}
            sx={{
              borderRadius: 999,
              px: 4,
              py: 1.6,
              backgroundColor: "#111",
              fontWeight: 700,
              fontSize: 15,
              letterSpacing: 0.4,
              boxShadow: "0 6px 16px rgba(0,0,0,.15)",
              "&:hover": {
                backgroundColor: "#333",
                boxShadow: "0 8px 18px rgba(0,0,0,.18)",
              },
            }}
          >
            SHOP THE COLLECTION
          </Button>
        </Box>

        {/* DESKTOP RIGHT IMAGE */}
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            width: "100%",
            height: "100%",
          }}
        >
          {rightImg ? (
            <Box
              component="img"
              src={rightImg}
              loading="eager"
              fetchPriority="high"
              alt="Couple wearing jewellery"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <Skeleton
              variant="rectangular"
              sx={{ width: "100%", height: "100%" }}
            />
          )}
        </Box>
      </Box>

      <section id="collection">
        <ShopByCategory />
      </section>
    </>
  );
}
